/**
 * MOTD Admin Page
 *
 * Allows admins to create, update, and deactivate the Message of the Day.
 * Displays history of past MOTDs with author and date.
 *
 * Requirements: 1.1, 1.2, 1.3, 1.5, 1.6, 1.7, 1.8, 6.9
 */

import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { restClient } from '../../api/rest-client';

interface HistoryRecord {
  message: string;
  version: string;
  updated_by: string;
  updated_at: string;
}

function formatDate(utcTimestamp: string): string {
  return new Date(utcTimestamp).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface ActiveMotd {
  message: string;
  version: string;
  updated_at: string;
  updated_by?: string;
  active: boolean;
}

export function MotdAdmin() {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [history, setHistory] = useState<HistoryRecord[]>([]);
  const [activeMotd, setActiveMotd] = useState<ActiveMotd | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const [motd, historyData] = await Promise.all([
          restClient.getMotd(),
          restClient.getMotdHistory(),
        ]);
        if (motd.active) {
          setActiveMotd(motd);
        }
        setHistory(historyData.history);
      } catch {
        setFeedback({ type: 'error', message: 'Failed to load MOTD data' });
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setFeedback(null);
      await restClient.putMotd(text);
      setText('');
      setFeedback({ type: 'success', message: 'MOTD saved successfully' });
      const [motd, historyData] = await Promise.all([
        restClient.getMotd(),
        restClient.getMotdHistory(),
      ]);
      setActiveMotd(motd.active ? motd : null);
      setHistory(historyData.history);
    } catch {
      setFeedback({ type: 'error', message: 'Failed to save MOTD' });
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    try {
      setSaving(true);
      setFeedback(null);
      await restClient.putMotd('');
      setText('');
      setActiveMotd(null);
      setFeedback({ type: 'success', message: 'MOTD cleared successfully' });
      const historyData = await restClient.getMotdHistory();
      setHistory(historyData.history);
    } catch {
      setFeedback({ type: 'error', message: 'Failed to clear MOTD' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50">
      <div className="bg-white shadow">
        <div className="container mx-auto px-4 py-4">
          <h1 className="text-3xl font-bold text-primary-900">Message of the Day</h1>
          <p className="text-primary-600 mt-1">Broadcast a message to all users</p>
        </div>
      </div>

      <div className="container mx-auto px-4 pt-3 pb-8">
        {/* Feedback */}
        {feedback && (
          <div
            className={`mb-4 p-3 rounded-md text-sm ${
              feedback.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {feedback.message}
          </div>
        )}

        {/* Editor */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="motd-text" className="block text-sm font-medium text-gray-700">
              MOTD Message
            </label>
            <span className="text-xs text-gray-500">
              Markdown supported.{' '}
              <a
                href="https://www.markdownguide.org/basic-syntax/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 underline hover:text-primary-800"
              >
                Formatting help
              </a>
            </span>
          </div>

          {/* Write / Preview tabs */}
          <div className="flex border-b border-gray-200 mb-3">
            <button
              type="button"
              onClick={() => setShowPreview(false)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                !showPreview
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Write
            </button>
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                showPreview
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Preview
            </button>
          </div>

          {showPreview ? (
            <div className="w-full min-h-[130px] px-3 py-2 border border-gray-200 rounded-md bg-gray-50 motd-markdown break-words">
              {text.trim() ? (
                <ReactMarkdown
                  components={{
                    a: ({ ...props }) => (
                      <a {...props} target="_blank" rel="noopener noreferrer" className="text-primary-600 underline hover:text-primary-800 break-all" />
                    ),
                  }}
                >
                  {text}
                </ReactMarkdown>
              ) : (
                <p className="text-gray-400 italic">Nothing to preview</p>
              )}
            </div>
          ) : (
            <textarea
              id="motd-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Enter your message of the day... (Markdown supported)"
            />
          )}
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleClear}
              disabled={saving}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
            >
              Clear MOTD
            </button>
          </div>
        </div>

        {/* Active MOTD */}
        {activeMotd && (
          <div className="bg-green-50 border border-green-200 rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-bold text-green-900 mb-2">Active MOTD</h2>
            <div className="text-green-900 motd-markdown break-words">
              <ReactMarkdown
                components={{
                  a: ({ ...props }) => (
                    <a {...props} target="_blank" rel="noopener noreferrer" className="text-green-700 underline hover:text-green-900 break-all" />
                  ),
                }}
              >
                {activeMotd.message}
              </ReactMarkdown>
            </div>
            <p className="text-sm text-green-700 mt-2">
              Published{activeMotd.updated_by ? ` by ${activeMotd.updated_by}` : ''} on {formatDate(activeMotd.updated_at)}
            </p>
          </div>
        )}

        {/* History */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">History</h2>
          {history.length === 0 ? (
            <p className="text-gray-500">No history yet</p>
          ) : (
            <div className="divide-y divide-gray-200">
              {history.map((item) => (
                <div key={item.version} className="py-3">
                  <div className="text-gray-900 motd-markdown break-words">
                    <ReactMarkdown
                      components={{
                        a: ({ ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer" className="text-primary-600 underline hover:text-primary-800 break-all" />
                        ),
                      }}
                    >
                      {item.message}
                    </ReactMarkdown>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    By {item.updated_by} on {formatDate(item.updated_at)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MotdAdmin;
